import type { PhilatelicItem } from '@briefmarken_katalog/types';

/**
 * Applies the document returned by a review request to the list entry the row
 * renders from.
 *
 * Rows read their state straight from the entries of the `items` / `suggestions`
 * arrays in the list ViewModel. Those arrays are `$state`, so their entries are
 * deep reactive proxies and patching one in place updates the row right away —
 * instead of leaving it stale until the SSE `catalog:updated` event triggers a
 * full refetch of both collections (which can lag, or be missed entirely while
 * the EventSource reconnects).
 *
 * Accepted suggestions move from the `suggestions` collection over to `items`;
 * patching one in place would leave a stale entry behind in the suggestions
 * column, so that transition is left to the refetch.
 */
export function applyReviewResult(target: PhilatelicItem, updated: PhilatelicItem | null): void {
  if (!updated || updated.review.state === 'Accepted') return;
  Object.assign(target, stripFullSizeImages(updated));
}

/**
 * The review endpoint answers with the complete document, while the list
 * endpoints project `pictures.base64` away and rows render from the thumbnails.
 * Dropping the full-size data keeps a patched entry the same weight as a
 * fetched one.
 */
function stripFullSizeImages(item: PhilatelicItem): PhilatelicItem {
  // The key is dropped rather than blanked, so `base64 ?? thumbnail` fallbacks
  // behave exactly as they do for a freshly fetched list entry.
  const withoutBase64 = (pictures: { base64: string; thumbnail?: string }[] | undefined) =>
    pictures?.map(({ thumbnail }) => ({ thumbnail }) as { base64: string; thumbnail?: string });

  const stripped: any = { ...item, pictures: withoutBase64((item as any).pictures) };
  if (Array.isArray((item as any).stamps)) {
    stripped.stamps = (item as any).stamps.map((stamp: any) => ({
      ...stamp,
      pictures: withoutBase64(stamp.pictures),
    }));
  }
  return stripped as PhilatelicItem;
}
